import { OnClickProp } from '@squareup/dex-types-shared-ui';
import { Box, Heading20 } from '@squareup/dex-ui-shared-base';
import clsx from 'clsx';
import React, { FC, PropsWithChildren, useCallback, useRef } from 'react';

import { NativeButton } from '../NativeButton';

import { animationDuration } from './TabAnimator';
import { TabItem } from './tab-types';
import styles from './tab.module.css';

interface TabsetProps {
  tabs: Array<TabItem>;
  currentTabId: string;
  onTabChanged: (tabId: string) => void;
}

interface TabsetItemProps {
  selected: boolean;
  last?: boolean | undefined;
  label: string;
}

const TabsetItem: FC<PropsWithChildren<TabsetItemProps & OnClickProp>> = ({
  children,
  selected,
  onClick,
  last,
  label,
}) => {
  return (
    <Box
      as="li"
      className={clsx(styles['tabset-item'])}
      margin={!last ? { right: '2x' } : undefined}
    >
      <NativeButton
        trackingId={'tabset-item'}
        onClick={onClick}
        role="tab"
        aria-label={label}
        aria-selected={selected || undefined}
      >
        <Heading20
          colorVariant="20"
          className={clsx(selected && styles.selected)}
          padding={{ bottom: '1.5x' }}
        >
          {children}
        </Heading20>
      </NativeButton>
      {selected && (
        <Box
          className={styles.underline}
          border={{
            radius: { topLeft: '6', topRight: '6', bottomLeft: '6' },
          }}
        ></Box>
      )}
    </Box>
  );
};

const Tabset: FC<TabsetProps> = ({ tabs, currentTabId, onTabChanged }) => {
  // We must wait for the animation from the tabs to finish before allowing
  // the user to switch again
  const tabsetChangeLockoutRef = useRef<boolean>(false);

  const onTabsetItemClicked = useCallback(
    (id: string) => {
      return () => {
        if (tabsetChangeLockoutRef.current) {
          return;
        }

        tabsetChangeLockoutRef.current = true;
        setTimeout(() => {
          tabsetChangeLockoutRef.current = false;
        }, animationDuration);
        onTabChanged(id);
      };
    },
    [onTabChanged]
  );

  return (
    <Box
      className={styles.tabset}
      as="ul"
      role="tablist"
      testId="tabset"
      border={{ line: { bottom: 'standard' } }}
      margin={{ bottom: '4x' }}
    >
      {tabs.map((item, i) => {
        return (
          <TabsetItem
            selected={item.id === currentTabId}
            key={item.id}
            onClick={onTabsetItemClicked(item.id)}
            last={i === tabs.length - 1}
            label={item.label}
          >
            {item.label}
          </TabsetItem>
        );
      })}
    </Box>
  );
};

export { Tabset };
